/**
 * Skynet · sk-gallery (módulo 1)
 * El carril se mueve con desplazamiento nativo (scroll-snap); este script solo añade:
 * contador, miniatura activa, flechas, teclado, salto a la imagen de la variante y zoom.
 * Sin JavaScript la galería sigue funcionando (se desliza con el dedo o la rueda).
 * La imagen de la variante elegida ya viene la primera desde Liquid (no hay salto al cargar).
 */

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class SkGallery extends HTMLElement {
  #index = 0;
  #target = -1;
  #targetTimer = 0;
  #observer = null;
  #ac = null;
  #zoomOpener = null;

  connectedCallback() {
    // Se puede desconectar y volver a conectar (editor, morph de Horizon): se monta de nuevo cada vez.
    this.track = this.querySelector('[data-sk-track]');
    if (!this.track) return;
    this.slides = [...this.track.querySelectorAll('.sk-gallery__slide')];
    this.thumbs = [...this.querySelectorAll('[data-sk-thumb]')];
    this.current = this.querySelector('[data-sk-current]');
    this.dialog = this.querySelector('[data-sk-dialog]');
    this.dialogImg = this.querySelector('[data-sk-dialog-img]');
    this.productId = Number(this.dataset.productId) || null;

    this.#ac?.abort();
    this.#ac = new AbortController();
    const signal = this.#ac.signal;

    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(this.#onIntersect, { root: this.track, threshold: 0.6 });
    this.slides.forEach((s) => this.#observer.observe(s));

    this.addEventListener('click', this.#onClick, { signal });
    this.track.addEventListener('keydown', this.#onKeydown, { signal });
    this.track.addEventListener('scrollend', this.#onScrollEnd, { signal });
    document.addEventListener('sk:variant-media', this.#onVariantMedia, { signal });
    this.dialog?.addEventListener('click', this.#onDialogClick, { signal });
    this.#listenToHorizonPicker(signal);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#ac?.abort();
    window.clearTimeout(this.#targetTimer);
  }

  goTo(index, animate = true) {
    if (!this.slides?.length) return;
    const i = (index + this.slides.length) % this.slides.length;
    const smooth = animate && !reduceMotion();
    // Mientras dura el desplazamiento suave, las diapositivas intermedias no cambian el contador.
    this.#target = smooth ? i : -1;
    window.clearTimeout(this.#targetTimer);
    if (smooth) this.#targetTimer = window.setTimeout(() => (this.#target = -1), 900);
    this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    this.#setIndex(i);
  }

  #setIndex(i) {
    if (i === this.#index && this.current?.textContent === String(i + 1)) return;
    this.#index = i;
    if (this.current) this.current.textContent = String(i + 1);
    this.thumbs.forEach((t, k) => {
      if (k === i) {
        t.setAttribute('aria-current', 'true');
        // Mantiene visible la miniatura activa sin mover la página.
        const list = t.closest('.sk-gallery__thumbs');
        if (list && list.scrollWidth > list.clientWidth) {
          const left = t.offsetLeft - (list.clientWidth - t.offsetWidth) / 2;
          list.scrollTo({ left, behavior: reduceMotion() ? 'instant' : 'smooth' });
        }
      } else {
        t.removeAttribute('aria-current');
      }
    });
    // Un vídeo que sale de la vista se pausa.
    this.slides.forEach((s, k) => {
      if (k !== i) s.querySelectorAll('video').forEach((v) => !v.paused && v.pause());
    });
  }

  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const i = Number(entry.target.dataset.index) || 0;
      if (this.#target >= 0 && i !== this.#target) continue;
      if (i === this.#target) this.#target = -1;
      this.#setIndex(i);
    }
  };

  #onScrollEnd = () => {
    this.#target = -1;
  };

  #onClick = (event) => {
    const prev = event.target.closest('[data-sk-prev]');
    const next = event.target.closest('[data-sk-next]');
    const thumb = event.target.closest('[data-sk-thumb]');
    const zoom = event.target.closest('[data-sk-zoom]');
    if (prev) this.goTo(this.#index - 1);
    else if (next) this.goTo(this.#index + 1);
    else if (thumb) this.goTo(Number(thumb.dataset.skThumb) || 0);
    else if (zoom) this.#openZoom(zoom);
  };

  #onKeydown = (event) => {
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) {
      event.preventDefault();
      this.goTo(this.#index + 1);
    } else if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) {
      event.preventDefault();
      this.goTo(this.#index - 1);
    }
  };

  #showMedia(mediaId) {
    const id = String(mediaId || '');
    if (!id) return;
    const i = this.slides.findIndex((s) => s.dataset.mediaId === id);
    if (i >= 0 && i !== this.#index) this.goTo(i);
  }

  /** Otro bloque (p. ej. las tarjetas de oferta) pide mostrar la imagen de una variante de este producto. */
  #onVariantMedia = (event) => {
    const pid = event.detail?.productId;
    if (pid && this.productId && pid !== this.productId) return;
    this.#showMedia(event.detail?.mediaId);
  };

  /** Si en la ficha se usa el selector de variantes de Horizon, la galería también lo sigue. */
  #listenToHorizonPicker(signal) {
    const section = this.closest('.shopify-section');
    if (!section) return;
    import('@shopify/events')
      .then(({ StandardEvents }) => {
        if (signal.aborted || !StandardEvents?.productSelect) return;
        section.addEventListener(
          StandardEvents.productSelect,
          (event) => {
            if (event.target?.closest?.('product-card')) return;
            Promise.resolve(event.promise)
              .then((result) => this.#showMedia(result?.detail?.resource?.featured_media?.id))
              .catch(() => {});
          },
          { signal }
        );
      })
      .catch(() => {});
  }

  #openZoom(button) {
    if (!this.dialog || !this.dialogImg || typeof this.dialog.showModal !== 'function') return;
    const img = button.querySelector('img');
    const full = button.dataset.full || '';
    // Primero la imagen que ya está cargada (instantáneo); la grande, cuando llegue.
    this.dialogImg.src = img?.currentSrc || full;
    this.dialogImg.alt = img?.alt || '';
    if (full && full !== this.dialogImg.src) {
      const hi = new Image();
      hi.src = full;
      hi.decode()
        .then(() => {
          if (this.dialog.open) this.dialogImg.src = full;
        })
        .catch(() => {});
    }
    this.#zoomOpener = button;
    this.dialog.showModal();
    this.dialog.addEventListener('close', this.#onDialogClose, { once: true });
  }

  #onDialogClick = (event) => {
    // Cerrar con la X o tocando la imagen o el fondo (la imagen no recibe el toque).
    if (event.target.closest('[data-sk-close]') || event.target === this.dialog) this.dialog.close();
  };

  #onDialogClose = () => {
    this.dialogImg.removeAttribute('src');
    this.#zoomOpener?.focus();
  };
}

if (!customElements.get('sk-gallery')) customElements.define('sk-gallery', SkGallery);
